import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmt, fmtCents } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCcw, Sparkles, CheckCircle2, ExternalLink, X, Undo2, Flame, Share2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Insights() {
  const [subs, setSubs] = useState(null);
  const [report, setReport] = useState(null);
  const [streak, setStreak] = useState(null);
  const [playbook, setPlaybook] = useState([]);
  const [selectedForBatch, setSelectedForBatch] = useState({});
  const [batchBusy, setBatchBusy] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const load = async () => {
    const [{ data: s }, { data: r }, { data: st }, { data: pb }] = await Promise.all([
      api.get("/insights/subscriptions"),
      api.get("/monthly_report"),
      api.get("/insights/cancel_streak"),
      api.get("/insights/playbook"),
    ]);
    setSubs(s);
    setReport(r);
    setStreak(st);
    setPlaybook(pb);
  };

  useEffect(() => {
    (async () => {
      await load();
      setLoading(false);
    })();
  }, []);

  const setStatus = async (merchant, status) => {
    await api.post(`/insights/subscriptions/${encodeURIComponent(merchant)}`, { status });
    setSubs((cur) => cur.map((s) => (s.merchant === merchant ? { ...s, status } : s)));
    if (status === "keep") toast.success("Marked as keep");
    if (status === "review") toast.info("Marked for review");
  };

  const confirmCancel = async (merchant) => {
    const prevCount = streak?.cancelled_this_month || 0;
    try {
      const { data } = await api.post(`/insights/subscriptions/${encodeURIComponent(merchant)}/confirm_cancel`);
      toast.success(`Nice — you're saving ${fmt(data.monthly_saved)}/mo (${fmt(data.annual_saved)}/yr)`);
      setCancelling(null);
      await load();
      // Reload streak and detect if we hit a milestone
      const { data: newStreak } = await api.get("/insights/cancel_streak");
      setStreak(newStreak);
      const milestones = [3, 5, 10, 20];
      if (newStreak.tier && milestones.includes(newStreak.cancelled_this_month) && newStreak.cancelled_this_month > prevCount) {
        setCelebration(newStreak);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log");
    }
  };

  const undoCancel = async (merchant) => {
    try {
      await api.post(`/insights/subscriptions/${encodeURIComponent(merchant)}/undo_cancel`);
      toast.info("Cancellation reverted");
      await load();
    } catch { toast.error("Could not undo"); }
  };

  if (loading) return <div className="p-6 text-slate-400">Loading…</div>;

  const activeSubs = subs.filter((s) => s.status !== "cancelled");
  const cancelledSubs = subs.filter((s) => s.status === "cancelled");
  const totalMonthly = activeSubs.reduce((a, b) => a + b.monthly_cost, 0);
  const totalSaved = cancelledSubs.reduce((a, b) => a + (b.monthly_cost || 0), 0);

  return (
    <div className="px-6 pt-10 pb-6">
      <h1 className="font-manrope text-2xl font-extrabold tracking-tight text-slate-900">Insights</h1>
      <p className="text-slate-500 text-sm mt-1">Recurring charges, monthly review, and priorities.</p>

      {/* Find money CTA */}
      <Link to="/app/find-money" className="mt-6 flex items-center justify-between bg-slate-900 text-white rounded-2xl px-5 py-4 hover:bg-slate-800 transition-colors">
        <div className="flex items-center gap-3">
          <Sparkles size={18} className="text-emerald-300" />
          <div>
            <div className="font-semibold">Find me money</div>
            <div className="text-xs text-slate-400">Run AI analysis</div>
          </div>
        </div>
        <span className="text-emerald-300 font-bold">→</span>
      </Link>

      {/* Money Coach CTA */}
      <Link to="/app/coach" data-testid="insights-coach-btn" className="mt-3 flex items-center justify-between bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 hover:bg-slate-100 transition-colors">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-2xl bg-slate-900 text-white flex items-center justify-center"><Sparkles size={14} className="text-emerald-400" /></span>
          <div>
            <div className="font-semibold text-slate-900">Ask the Money Coach</div>
            <div className="text-xs text-slate-500">Chat with your data</div>
          </div>
        </div>
        <span className="text-slate-400 font-bold">→</span>
      </Link>

      {/* Monthly report */}
      <div className="mt-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 font-semibold"><RefreshCcw size={12} /> This month</div>
        <div className="mt-3 rounded-2xl border border-slate-200 p-5">
          <p className="text-slate-800 leading-relaxed" data-testid="monthly-summary">{report.summary}</p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Stat label="Income" v={fmt(report.cash_flow.income)} />
            <Stat label="Expenses" v={fmt(report.cash_flow.expenses)} />
            <Stat label="Net" v={fmt(report.cash_flow.net)} tone={report.cash_flow.net >= 0 ? "pos" : "neg"} />
          </div>
        </div>
        <div className="mt-5 rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Your 3 priorities for next month</div>
          <ol className="mt-3 space-y-2">
            {report.priorities.map((p, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 shrink-0 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="text-slate-800 text-sm leading-relaxed">{p}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Subscriptions */}
      <div className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-manrope text-xl font-bold tracking-tight text-slate-900">Recurring & subscriptions</h2>
          <div className="text-sm text-slate-500">{fmt(totalMonthly)}/mo</div>
        </div>
        <p className="text-slate-500 text-sm mt-1">We detected these repeating charges.</p>

        {/* Cancel Playbook */}
        {playbook.filter((p) => p.you_have_it && p.your_status !== "cancelled").length > 0 && (
          <div className="mt-4 rounded-2xl border border-slate-900 bg-slate-900 text-white p-5" data-testid="cancel-playbook">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-emerald-300 text-[11px] uppercase tracking-widest font-bold">🔥 Cancel Playbook</div>
                <div className="mt-1 font-manrope font-bold">Most-cancelled by MoneyWise users</div>
              </div>
              {Object.values(selectedForBatch).filter(Boolean).length > 1 && (
                <div className="text-[11px] text-emerald-300 font-bold">{Object.values(selectedForBatch).filter(Boolean).length} selected</div>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-300 leading-relaxed">Pick one — or select multiple to cancel in a batch.</p>
            <div className="mt-3 space-y-2">
              {playbook.filter((p) => p.you_have_it && p.your_status !== "cancelled").slice(0, 6).map((p) => {
                const selected = !!selectedForBatch[p.merchant];
                return (
                  <div key={p.merchant} className={`rounded-xl border p-3 flex items-center gap-3 transition-colors ${selected ? "bg-emerald-500/10 border-emerald-400" : "bg-white/5 border-white/10 hover:bg-white/10"}`} data-testid={`playbook-item-${p.merchant.replace(/\s+/g, '-')}`}>
                    <button
                      data-testid={`playbook-select-${p.merchant.replace(/\s+/g, '-')}`}
                      onClick={() => setSelectedForBatch((s) => ({ ...s, [p.merchant]: !s[p.merchant] }))}
                      className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${selected ? "bg-emerald-400 border-emerald-400" : "border-white/30 hover:border-white/60"}`}
                      aria-label="Select"
                    >
                      {selected && <Check size={14} strokeWidth={3} className="text-slate-900" />}
                    </button>
                    <button
                      onClick={() => setCancelling({
                        merchant: p.merchant,
                        monthly_cost: p.your_monthly_cost,
                        annual_cost: (p.your_monthly_cost || 0) * 12,
                        cancel_guide: p.cancel_guide,
                      })}
                      className="flex-1 text-left min-w-0"
                    >
                      <div className="font-manrope font-bold text-sm text-white truncate">{p.merchant}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{p.cancel_count} user{p.cancel_count === 1 ? "" : "s"} cancelled · you pay {fmtCents(p.your_monthly_cost)}/mo</div>
                    </button>
                    <div className="text-emerald-400 font-bold text-sm shrink-0">→</div>
                  </div>
                );
              })}
            </div>
            {Object.values(selectedForBatch).filter(Boolean).length > 1 && (
              <button
                data-testid="batch-cancel-btn"
                disabled={batchBusy}
                onClick={async () => {
                  const merchants = Object.entries(selectedForBatch).filter(([, v]) => v).map(([k]) => k);
                  setBatchBusy(true);
                  try {
                    const { data } = await api.post("/insights/subscriptions_batch_cancel", { merchants });
                    toast.success(`Cancelled ${data.cancelled_count} — saving ${fmt(data.total_monthly_saved)}/mo (${fmt(data.total_annual_saved)}/yr)`);
                    setSelectedForBatch({});
                    await load();
                    const { data: newStreak } = await api.get("/insights/cancel_streak");
                    setStreak(newStreak);
                    if (newStreak.tier) setCelebration(newStreak);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Batch cancel failed");
                  }
                  setBatchBusy(false);
                }}
                className="mt-4 w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full py-3 font-semibold active:scale-[0.98] transition-transform duration-150 disabled:opacity-60"
              >
                {batchBusy ? "Cancelling…" : `Cancel ${Object.values(selectedForBatch).filter(Boolean).length} in a batch`}
              </button>
            )}
          </div>
        )}

        {totalSaved > 0 && (
          <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3" data-testid="cancelled-savings-summary">
            <CheckCircle2 className="text-emerald-600 shrink-0" size={20} />
            <div className="text-sm">
              <div className="font-manrope font-bold text-emerald-900">You've cancelled {cancelledSubs.length} — saving {fmt(totalSaved)}/mo</div>
              <div className="text-emerald-700 text-xs mt-0.5">≈ {fmt(totalSaved * 12)}/year kept in your pocket</div>
            </div>
          </div>
        )}

        {streak && streak.cancelled_this_month > 0 && (
          <StreakCard streak={streak} onShare={() => setCelebration(streak)} />
        )}

        <div className="mt-4 space-y-3">
          {activeSubs.length === 0 && cancelledSubs.length === 0 && <div className="text-slate-400 text-sm">No recurring charges detected yet.</div>}
          {activeSubs.map((s) => (
            <div key={s.merchant} className="rounded-2xl border border-slate-200 p-4" data-testid={`sub-${s.merchant.replace(/\s+/g, '-')}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-manrope font-bold text-slate-900 truncate">{s.merchant}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtCents(s.monthly_cost)}/mo · {fmtCents(s.annual_cost)}/yr · last: {s.last_transaction_date}</div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${s.status === "review" ? "bg-amber-100 text-amber-700" : s.status === "keep" ? "bg-slate-100 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{s.status || "active"}</span>
              </div>
              <div className="mt-3 text-sm text-slate-600">Are you still using this?</div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <SubBtn onClick={() => setStatus(s.merchant, "keep")} label="Keep" testId={`keep-${s.merchant.replace(/\s+/g, '-')}`} />
                <SubBtn onClick={() => setStatus(s.merchant, "review")} label="Review" testId={`review-${s.merchant.replace(/\s+/g, '-')}`} />
                <SubBtn onClick={() => setCancelling(s)} label="Cancel" destructive testId={`cancel-${s.merchant.replace(/\s+/g, '-')}`} />
              </div>
            </div>
          ))}

          {cancelledSubs.map((s) => (
            <div key={s.merchant} className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid={`sub-cancelled-${s.merchant.replace(/\s+/g, '-')}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                    <div className="font-manrope font-bold text-slate-900 truncate line-through decoration-slate-400">{s.merchant}</div>
                  </div>
                  <div className="text-xs text-emerald-700 mt-1 font-semibold">Saving {fmtCents(s.monthly_cost)}/mo · {fmtCents(s.annual_cost)}/yr</div>
                </div>
                <button data-testid={`undo-cancel-${s.merchant.replace(/\s+/g, '-')}`} onClick={() => undoCancel(s.merchant)} className="shrink-0 text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 transition-colors">
                  <Undo2 size={12} /> Undo
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cancel modal */}
      <Dialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          {cancelling && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 font-semibold">Cancel subscription</div>
                <DialogTitle className="font-manrope text-xl font-extrabold text-slate-900 tracking-tight">{cancelling.merchant}</DialogTitle>
              </DialogHeader>

              <div className="mt-2 rounded-2xl bg-slate-900 text-white p-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">You'd save</div>
                <div className="font-manrope text-3xl font-extrabold tracking-tighter tabular-nums mt-1">{fmt(cancelling.monthly_cost)}<span className="text-slate-400 text-sm font-semibold">/mo</span></div>
                <div className="text-slate-400 text-xs mt-0.5">≈ {fmt(cancelling.annual_cost)}/year</div>
              </div>

              {cancelling.cancel_guide ? (
                <>
                  <div className="mt-4 text-sm text-slate-700 leading-relaxed">
                    <b>How to cancel:</b> {cancelling.cancel_guide.steps}
                  </div>
                  <a
                    href={cancelling.cancel_guide.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cancel-guide-link"
                    className="mt-3 w-full rounded-full border border-slate-300 py-3 font-semibold text-slate-900 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    Open cancel page <ExternalLink size={14} />
                  </a>
                </>
              ) : (
                <div className="mt-4 text-sm text-slate-600 leading-relaxed">
                  Log in to <b>{cancelling.merchant}</b> → Account settings → look for "Subscription" or "Membership" → Cancel. Screenshots or a confirmation email will help you remember.
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button data-testid="cancel-dismiss-btn" onClick={() => setCancelling(null)} className="rounded-full py-3 text-sm font-semibold bg-slate-100 hover:bg-slate-200 text-slate-800 transition-colors flex items-center justify-center gap-1"><X size={14} /> Not yet</button>
                <button data-testid="cancel-confirm-btn" onClick={() => confirmCancel(cancelling.merchant)} className="rounded-full py-3 text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-slate-900 transition-colors flex items-center justify-center gap-1"><CheckCircle2 size={14} /> I cancelled it</button>
              </div>
              <p className="mt-3 text-[11px] text-slate-400 text-center leading-relaxed">Confirming logs the saved amount to your Find Me Money plan.</p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Streak celebration modal */}
      <Dialog open={!!celebration} onOpenChange={(o) => !o && setCelebration(null)}>
        <DialogContent className="max-w-sm rounded-3xl overflow-hidden p-0">
          {celebration && (
            <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-white p-8 text-center relative">
              <div className="absolute inset-0 opacity-30 pointer-events-none">
                {[...Array(24)].map((_, i) => (
                  <span key={i} className="absolute text-2xl animate-pulse" style={{ left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%`, animationDelay: `${i * 100}ms` }}>✦</span>
                ))}
              </div>
              <div className="relative">
                <div className="text-6xl">{celebration.tier?.emoji || "🔥"}</div>
                <div className="mt-4 text-xs uppercase tracking-widest text-emerald-300 font-semibold">{celebration.tier?.name || "Streak"}</div>
                <div className="mt-1 font-manrope text-3xl font-extrabold tracking-tighter">{celebration.cancelled_this_month}-cancel streak!</div>
                <div className="mt-4 rounded-2xl bg-white/10 backdrop-blur px-4 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-300 font-semibold">You're saving</div>
                  <div className="font-manrope text-4xl font-extrabold tracking-tighter tabular-nums text-emerald-400">{fmt(celebration.monthly_saved)}<span className="text-slate-400 text-base font-semibold">/mo</span></div>
                  <div className="text-slate-400 text-xs mt-0.5">≈ {fmt(celebration.annual_saved)}/year</div>
                </div>
                <button
                  data-testid="celebration-share-btn"
                  onClick={async () => {
                    const text = `I just cancelled ${celebration.cancelled_this_month} subscriptions with MoneyWise AI — saving ${fmt(celebration.annual_saved)}/year. Find your money too.`;
                    try {
                      if (navigator.share) await navigator.share({ text });
                      else { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
                    } catch {}
                  }}
                  className="mt-6 w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full py-3 font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Share2 size={16} /> Share the win
                </button>
                <button data-testid="celebration-dismiss-btn" onClick={() => setCelebration(null)} className="mt-3 w-full text-slate-400 text-sm hover:text-white transition-colors">Continue</button>
                {celebration.next_tier && (
                  <div className="mt-4 text-[11px] text-slate-400">Next up: <b className="text-white">{celebration.next_tier.name}</b> at {celebration.next_tier.count} cancels {celebration.next_tier.emoji}</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StreakCard({ streak, onShare }) {
  const cur = streak.cancelled_this_month;
  const next = streak.next_tier;
  const tier = streak.tier;
  const pct = next ? Math.min(100, (cur / next.count) * 100) : 100;
  return (
    <div className="mt-3 rounded-2xl bg-gradient-to-r from-orange-50 to-amber-50 border border-amber-200 p-4" data-testid="streak-card">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-500/10 flex items-center justify-center text-2xl">{tier?.emoji || "🎯"}</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">{tier ? tier.name : "This month"}</div>
            <div className="font-manrope font-extrabold text-slate-900 leading-tight">
              {cur} cancel{cur === 1 ? "" : "s"} this month
            </div>
          </div>
        </div>
        {tier && (
          <button data-testid="streak-share-btn" onClick={onShare} className="shrink-0 rounded-full bg-white border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50 transition-colors flex items-center gap-1"><Share2 size={12} /> Share</button>
        )}
      </div>
      {next && (
        <div className="mt-3">
          <div className="h-1.5 rounded-full bg-amber-100 overflow-hidden">
            <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1.5 text-[11px] text-amber-700 flex justify-between">
            <span>{next.count - cur} to <b>{next.name}</b> {next.emoji}</span>
            <span className="font-semibold tabular-nums">{cur}/{next.count}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, v, tone }) {
  const c = tone === "pos" ? "text-emerald-600" : tone === "neg" ? "text-red-500" : "text-slate-900";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className={`font-manrope text-lg font-extrabold tracking-tight tabular-nums ${c}`}>{v}</div>
    </div>
  );
}
function SubBtn({ onClick, label, destructive, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`rounded-full py-2 text-xs font-semibold transition-colors duration-200 ${destructive ? "bg-red-50 text-red-700 hover:bg-red-100" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{label}</button>
  );
}
